import { resolveToolApprovalRenderer } from "#components/ui/tool-approval/renderers/index.ts";
import type { ToolApprovalDisplay } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { ToolApprovalCardBody } from "#components/ui/tool-approval/ToolApprovalCardBody.tsx";
import { providerOf } from "#features/conversations/components/prepared-action/calendarEventFields.ts";
import { DraftActions, DraftCardProvider, DraftFooter } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { INTEGRATION_MANIFESTS } from "#features/conversations/integrations/index.ts";
import { isObject, readString, type Fields } from "#features/conversations/lib/untypedFields.ts";
import { useFormatDate } from "#hooks/useFormatDate.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { ToolApprovalEditError } from "#types/ToolApproval.ts";
import type { PreparedActionCard as PreparedActionState, PreparedActionStatus, ToolApprovalCall } from "#types/types.ts";
import { Flex, Text } from "@hopper-ui/components";
import { IconCheck, IconTriangleExclamation } from "@intentui/icons";
import classNames from "classnames";
import { useState } from "react";

interface AnsweredActionCardProps {
    // With the display context the approval prompt showed, when the caller still has it; without it the card shows the draft alone.
    call: Pick<ToolApprovalCall, "toolCallId" | "toolName" | "args" | "display">;
    status: PreparedActionStatus;
    executedAt?: string | null;
    // True once an approved draft has waited a minute with no saved result.
    isUnsettled?: boolean;
    // True while a turn runs, so an approved draft still on its way out shows its Send button spinning.
    isSending?: boolean;
}

interface Props {
    state: PreparedActionState;
    isUnsettled?: boolean;
    display?: ToolApprovalDisplay;
}

interface StatusLabel {
    tone?: "sent" | "failed";
    text: string;
}

const JUST_NOW_WINDOW_MS = 60_000;

// Core's draft kinds and the tool each one sends through.
const TOOL_BY_KIND: Record<string, string> = {
    good_vibe: "send_good_vibe",
    feedback_reply: "reply_to_feedback",
    slack_reply: "send_slack_message",
    slack_message: "send_slack_message",
    calendar_event: "create_calendar_event"
};

const NO_EDITS: Record<string, unknown> = {};
const NO_EDIT_ERRORS: ToolApprovalEditError[] = [];
const NO_EDIT = () => {};

// Who or where the draft went, from the context the prompt showed; the generic word without it.
function useSentLabel(toolName: string, args: Fields, display: Fields): string {
    const { tShared, i18n } = useSharedTranslation();
    const recipients = (Array.isArray(display.recipients) ? display.recipients : []).flatMap(entry => {
        const name = isObject(entry) ? readString(entry, "name") : null;

        return name === null ? [] : [name];
    });
    const channelName = readString(display, "channel_name");

    if (toolName === "reply_to_feedback") {
        return tShared("preparedAction.replySent");
    }
    if (toolName === "create_calendar_event") {
        const provider = providerOf(args);
        if (provider !== null) {
            return tShared("preparedAction.bookedInCalendar", { provider: INTEGRATION_MANIFESTS[provider].displayName });
        }
        const subject = readString(args, "subject")?.trim();

        return subject ? tShared("preparedAction.inviteSentFor", { subject }) : tShared("preparedAction.inviteSent");
    }
    if (toolName === "send_good_vibe" && recipients.length > 0) {
        return tShared("preparedAction.sentTo", { recipient: new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(recipients) });
    }
    if (toolName === "send_slack_message" && channelName !== null) {
        return tShared("preparedAction.sentTo", { recipient: `#${channelName}` });
    }

    return tShared("preparedAction.sent");
}

// Null for a status with nothing to say, so the card shows no footer.
function useStatusLabel({ call, status, executedAt, isUnsettled }: AnsweredActionCardProps): StatusLabel | null {
    const { tShared } = useSharedTranslation();
    const formatDate = useFormatDate();
    const sentLabel = useSentLabel(call.toolName, call.args, call.display ?? {});

    switch (status) {
        case "failed":
            return { tone: "failed", text: tShared("preparedAction.failed") };
        case "approved":
            return isUnsettled ? { tone: "failed", text: tShared("preparedAction.notConfirmed") } : { text: tShared("preparedAction.awaitingOutcome") };
        case "discarded":
            return { text: tShared("preparedAction.discarded") };
        case "cancelled":
            return { text: tShared("preparedAction.cancelled") };
        case "sent": {
            const executedTime = executedAt ? Date.parse(executedAt) : Number.NaN;
            const when = Date.now() - executedTime < JUST_NOW_WINDOW_MS ? tShared("preparedAction.justNow") : formatDate(executedAt, { showRelativeToday: true });

            return { tone: "sent", text: when !== null ? `${sentLabel} · ${when}` : sentLabel };
        }
        default:
            return null;
    }
}

// The failure keeps the text colour; the danger hue marks only its icon.
function StatusLine({ tone, text }: StatusLabel) {
    return (
        <Text size="xs" className={classNames("wai-pa-status", tone && `wai-pa-status-${tone}`)}>
            {tone === "sent" && <IconCheck aria-hidden="true" />}
            {tone === "failed" && <IconTriangleExclamation aria-hidden="true" />}
            <span className="wai-pa-status-text">{text}</span>
        </Text>
    );
}

// A call once its approval is answered: the card as it went out, with its status in the footer. While the call is
// pending, the approval prompt holds it.
export function AnsweredActionCard(props: AnsweredActionCardProps) {
    const { call, status, isUnsettled, isSending } = props;
    const { tShared } = useSharedTranslation();
    const statusLabel = useStatusLabel(props);
    const statusLine = statusLabel && <StatusLine {...statusLabel} />;
    // A card that gives way to the generic content has no footer, so the status sits under it.
    const [hasFailedCard, setHasFailedCard] = useState(false);
    const renderer = hasFailedCard ? null : resolveToolApprovalRenderer(call);
    const sendingLabels = isSending && status === "approved" && !isUnsettled ? renderer?.decisionLabels : undefined;
    const draftCard = sendingLabels
        ? {
            mode: "sending" as const,
            footer: (
                <DraftFooter
                    actions={<DraftActions sendLabel={tShared(sendingLabels.allow)} discardLabel={tShared(sendingLabels.deny)} isSending onSend={NO_EDIT} onDiscard={NO_EDIT} />}
                />
            )
        }
        : { mode: "settled" as const, footer: statusLine && <DraftFooter status={statusLine} /> };

    return (
        <Flex direction="column" gap="stack-xs" role="group" aria-label={tShared("preparedAction.cardLabel")}>
            <DraftCardProvider value={draftCard}>
                <ToolApprovalCardBody
                    call={call}
                    isBatch={false}
                    edits={NO_EDITS}
                    onEditArgs={NO_EDIT}
                    editErrors={NO_EDIT_ERRORS}
                    onCardError={() => setHasFailedCard(true)}
                />
            </DraftCardProvider>
            {!renderer && statusLine}
        </Flex>
    );
}

// A drafted action from the weekly brief, once its approval is answered.
export function PreparedActionCard({ state, isUnsettled, display = null }: Props) {
    const action = isObject(state.action) ? state.action : {};
    const toolName = TOOL_BY_KIND[readString(action, "kind") ?? ""] ?? "";

    return (
        <AnsweredActionCard
            call={{ toolCallId: state.toolCallId, toolName, args: action, display }}
            status={state.status}
            executedAt={state.executedAt}
            isUnsettled={isUnsettled}
        />
    );
}
